import moment from 'moment';

import { types as passengerTypes } from 'types/passengers';
import { INFANT_DETAIL_KEYS, INFANT_FIRST_NAME_KEY, INFANT_LAST_NAME_KEY, INFANT_UUID } from 'constants/passengers';

export const getInfantCountFromSearch = (search) => Number(new URLSearchParams(search).get('infants')) || 0;

export const getInfantPassengersFromDetails = ({ additionalDetails = [] }) =>
  Object.values(
    additionalDetails
      .filter(({ key, passengerRef }) => passengerRef && INFANT_DETAIL_KEYS.includes(key))
      .reduce((infantAcc, { passengerRef, key, value }) => {
        if (!infantAcc[passengerRef]) {
          infantAcc[passengerRef] = { passengerRef };
        }

        infantAcc[passengerRef][key] = value;

        return infantAcc;
      }, {})
  );

const getPassengerId = ({ id, passengerId } = {}) => id || passengerId;

const getPassengerTravelDocumentRequired = ({ isTravelDocumentRequired, travelDocumentsRequired = [] } = {}) =>
  Boolean(isTravelDocumentRequired || travelDocumentsRequired.some(({ required }) => required));

const getReadyToTravelForPassengerUuid = ({ isEligibleToTravelPerTravelDocs = [], passengerUUID }) =>
  isEligibleToTravelPerTravelDocs.find((doc) => doc.passengerUUID === passengerUUID)?.readyToTravel;

const getProductPassengerId = ({ passenger, passengerId } = {}) => passenger?.id || passengerId;

const isActiveProductForPassenger = ({ passengerId, product }) =>
  getProductPassengerId(product) === passengerId &&
  product?.isActive !== false &&
  !product?.isCancelled &&
  !product?.cancelled &&
  !product?.provisional;

const passengerHasFutureTrip = ({ booking, passengerId }) =>
  [...(booking?.outbound || []), ...(booking?.inbound || [])].some(
    (leg) =>
      !leg.cancelled &&
      moment(leg.timing?.isoDate).isAfter(moment()) &&
      (leg.products || []).some((product) => isActiveProductForPassenger({ passengerId, product }))
  );

export const getInfantPassengersWithTravelDocumentStatus = ({
  additionalDetails = [],
  isEligibleToTravelPerTravelDocs = [],
  passengers = []
}) => {
  const passengersById = passengers.reduce((acc, passenger) => {
    const passengerId = getPassengerId(passenger);

    if (passengerId) {
      acc[passengerId] = passenger;
    }

    return acc;
  }, {});

  return getInfantPassengersFromDetails({ additionalDetails }).map((infant) => {
    const isTravelDocumentRequired = getPassengerTravelDocumentRequired(passengersById[infant.passengerRef]);
    const hasTravelDocumentBeenProvided = getReadyToTravelForPassengerUuid({
      isEligibleToTravelPerTravelDocs,
      passengerUUID: infant[INFANT_UUID]
    });

    return {
      ...infant,
      isTravelDocumentRequired,
      hasTravelDocumentBeenProvided:
        isTravelDocumentRequired && hasTravelDocumentBeenProvided === undefined ? false : hasTravelDocumentBeenProvided
    };
  });
};

export const getInfantsMissingUuidForTravelDocuments = ({ booking }) => {
  const passengers = booking?.passengers || [];
  const passengersById = passengers.reduce((acc, passenger) => {
    const passengerId = getPassengerId(passenger);

    if (passengerId) {
      acc[passengerId] = passenger;
    }

    return acc;
  }, {});

  return getInfantPassengersFromDetails({ additionalDetails: booking?.additionalDetails }).reduce((acc, infant) => {
    const parentPassenger = passengersById[infant.passengerRef];

    if (
      infant[INFANT_UUID] ||
      !getPassengerTravelDocumentRequired(parentPassenger) ||
      !passengerHasFutureTrip({ booking, passengerId: infant.passengerRef })
    ) {
      return acc;
    }

    return [
      ...acc,
      {
        ...infant,
        parentPassenger
      }
    ];
  }, []);
};

export const buildInfantPassengerForTravelDocuments = ({ infant, parentPassenger }) => {
  const parentPassengerId = getPassengerId(parentPassenger) || infant.passengerRef;

  if (!infant[INFANT_UUID] || !parentPassengerId) {
    return null;
  }

  return {
    firstName: infant[INFANT_FIRST_NAME_KEY] || '',
    guardianUUID: parentPassenger?.uuid,
    hasTravelDocumentBeenProvided: infant.hasTravelDocumentBeenProvided,
    id: parentPassengerId,
    isInfant: true,
    isTravelDocumentRequired: infant.isTravelDocumentRequired,
    lastName: infant[INFANT_LAST_NAME_KEY] || '',
    parentPassengerId,
    type: passengerTypes.INFANT_ONLY,
    uuid: infant[INFANT_UUID]
  };
};

export const getPassengersWithInfantsForTravelDocuments = ({ booking, isEligibleToTravelPerTravelDocs }) => {
  if (!booking) {
    return [];
  }

  const passengers = booking.passengers || [];

  const passengersById = passengers.reduce((acc, passenger) => {
    acc[passenger.id] = passenger;

    return acc;
  }, {});
  const infantsByPassengerId = getInfantPassengersWithTravelDocumentStatus({
    additionalDetails: booking.additionalDetails,
    isEligibleToTravelPerTravelDocs,
    passengers
  }).reduce((acc, infant) => {
    const infantPassenger = buildInfantPassengerForTravelDocuments({
      infant,
      parentPassenger: passengersById[infant.passengerRef]
    });

    if (infantPassenger) {
      acc[infant.passengerRef] = infantPassenger;
    }

    return acc;
  }, {});

  return passengers.flatMap((passenger) => {
    const infant = infantsByPassengerId[passenger.id];

    return infant ? [passenger, infant] : [passenger];
  });
};
